import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as siteTypeService from "../../services/siteTypeService";
import { SiteTypeItem } from "../../services/siteTypeService";

interface SiteTypeState {
    items: SiteTypeItem[];
    loading: boolean;
    error: string | null;
}

const initialState: SiteTypeState = {
    items: [],
    loading: false,
    error: null,
};

// Async thunk to fetch site types from API
export const fetchSiteTypes = createAsyncThunk(
    "siteTypes/fetchSiteTypes",
    async () => {
        const response: SiteTypeItem[] = await siteTypeService.getAllSiteTypes();
        return response;
    }
);

// Async thunk to create a new site type
export const createSiteType = createAsyncThunk(
    "siteTypes/createSiteType",
    async (data: siteTypeService.SiteTypeCreatePayload) => {
        const response = await siteTypeService.createSiteType(data);
        return response;
    }
);

// Async thunk to update an existing site type
export const updateSiteType = createAsyncThunk(
    "siteTypes/updateSiteType",
    async ({ id, data }: { id: string; data: siteTypeService.SiteTypeUpdatePayload }) => {
        const response = await siteTypeService.updateSiteType(id, data);
        return response;
    }
);

// Async thunk to delete a site type
export const deleteSiteType = createAsyncThunk(
    "siteTypes/deleteSiteType",
    async (id: string) => {
        await siteTypeService.deleteSiteType(id);
        return id;
    }
);

const siteTypeSlice = createSlice({
    name: "SiteTypes",
    initialState,
    reducers: {
        clearError(state) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch site types
            .addCase(fetchSiteTypes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSiteTypes.fulfilled, (state, action) => {
                state.loading = false;
                // Group: Global types first (tenantId === null), then tenant types
                // Sort alphabetically within each group
                // Filter out deleted items and items with missing names
                const activeTypes = (action.payload || []).filter((st: SiteTypeItem) => !st.isDeleted && st && st.name && typeof st.name === 'string');
                const globalTypes = activeTypes
                    .filter((st: SiteTypeItem) => st.tenantId === null)
                    .sort((a: SiteTypeItem, b: SiteTypeItem) => (a.name || '').localeCompare(b.name || ''));
                const tenantTypes = activeTypes
                    .filter((st: SiteTypeItem) => st.tenantId !== null)
                    .sort((a: SiteTypeItem, b: SiteTypeItem) => (a.name || '').localeCompare(b.name || ''));
                state.items = [...globalTypes, ...tenantTypes];
            })
            .addCase(fetchSiteTypes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch site types";
            })
            // Create site type
            .addCase(createSiteType.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createSiteType.fulfilled, (state, action) => {
                state.loading = false;
                // Don't add to state here - let fetchSiteTypes handle it
                // This ensures we get the latest data from the API response
                // The component will call fetchSiteTypes after creation
            })
            .addCase(createSiteType.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to create site type";
            })
            // Update site type
            .addCase(updateSiteType.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateSiteType.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.items.findIndex((st) => st.id === action.payload.id);
                if (index !== -1) {
                    state.items[index] = action.payload;
                }
                // Re-sort after update
                // Filter out items with missing names and ensure name is a string before sorting
                const validItems = state.items.filter((st: SiteTypeItem) => st && st.name && typeof st.name === 'string');
                const globalTypes = validItems
                    .filter((st: SiteTypeItem) => st.tenantId === null)
                    .sort((a: SiteTypeItem, b: SiteTypeItem) => (a.name || '').localeCompare(b.name || ''));
                const tenantTypes = validItems
                    .filter((st: SiteTypeItem) => st.tenantId !== null)
                    .sort((a: SiteTypeItem, b: SiteTypeItem) => (a.name || '').localeCompare(b.name || ''));
                state.items = [...globalTypes, ...tenantTypes];
            })
            .addCase(updateSiteType.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to update site type";
            })
            // Delete site type
            .addCase(deleteSiteType.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteSiteType.fulfilled, (state, action) => {
                state.loading = false;
                state.items = state.items.filter((st) => st.id !== action.payload);
            })
            .addCase(deleteSiteType.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to delete site type";
            });
    },
});

export const { clearError } = siteTypeSlice.actions;
export default siteTypeSlice.reducer;

// Selectors
export const selectSiteTypeList = (state: any) => state.SiteTypes?.items || [];
export const selectSiteTypeLoading = (state: any) => state.SiteTypes?.loading || false;
export const selectSiteTypeError = (state: any) => state.SiteTypes?.error || null;
export const selectSiteTypeById = (state: any, id: string) =>
    state.SiteTypes?.items?.find((st: SiteTypeItem) => st.id === id) || null;
